import React, { useEffect, useState } from "react";
import { Box, Container, Card, Typography, Grid, Paper, Button, TextField, Alert, CircularProgress } from "@mui/material";
import { withStyles } from "@mui/styles";
import { styleSheet } from "./style.js";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AIDiagnostics from "./aiDiagnostics";
import { Login as LoginIcon } from "@mui/icons-material";

function Development(props) {
  const { classes } = props;
  const adminAuth = useSelector((state) => state.adminAuth);
  const navigate = useNavigate();
  
  // Claude server authentication state
  const [claudeToken, setClaudeToken] = useState(localStorage.getItem('claudeToken'));
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Check if user is logged in and has appropriate access
  const isLoggedIn = !!adminAuth?.adminDetail;
  const roleName = adminAuth?.adminDetail?.role?.name;
  const hasAdminRole = roleName === "Admin";
  const hasDevelopmentPermission = adminAuth?.adminDetail?.role?.role_permissions?.find(
    (permission) => permission.permission.slug === "development" || permission.permission.slug === "admin"
  );
  
  const isAdmin = hasAdminRole || hasDevelopmentPermission;
  
  // Login to Claude server
  const handleClaudeLogin = async () => {
    try {
      setLoginLoading(true);
      setLoginError('');
      
      const response = await fetch(`${process.env.REACT_APP_AI_DIAGNOSTIC_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: 'peter',
          password: 'password'
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Store the token
      localStorage.setItem('claudeToken', data.token);
      setClaudeToken(data.token);
      
      console.log('✅ Successfully logged into Claude server');
    } catch (err) {
      console.error('❌ Claude server login error:', err);
      setLoginError(err.message || 'Login failed');
    } finally {
      setLoginLoading(false);
    }
  };
  


  // Debug information
  console.log("Development Module - Admin Auth State:", adminAuth);
  console.log("Admin Detail:", adminAuth?.adminDetail);
  console.log("Role:", adminAuth?.adminDetail?.role);
  console.log("Role Name:", adminAuth?.adminDetail?.role?.name);
  console.log("Role Permissions:", adminAuth?.adminDetail?.role?.role_permissions);
  console.log("Has Admin Role:", hasAdminRole);
  console.log("Has Development Permission:", hasDevelopmentPermission);
  console.log("Is Admin:", isAdmin);

  if (!isLoggedIn) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Not Logged In
        </Typography>
        <Typography variant="body1">
          Please log in to access the Development module.
        </Typography>
      </Container>
    );
  }

  if (!isAdmin) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1">
          You do not have permission to access the Development module.
        </Typography>
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Debug Information:
          </Typography>
          <Typography variant="body2" component="pre" sx={{ fontSize: '12px', overflow: 'auto' }}>
            {JSON.stringify({
              hasAdminAuth: !!adminAuth,
              hasAdminDetail: !!adminAuth?.adminDetail,
              hasRole: !!adminAuth?.adminDetail?.role,
              roleName: adminAuth?.adminDetail?.role?.name,
              hasAdminRole: hasAdminRole,
              hasDevelopmentPermission: hasDevelopmentPermission,
              rolePermissions: adminAuth?.adminDetail?.role?.role_permissions?.map(p => p.permission.slug) || [],
              isAdmin: isAdmin
            }, null, 2)}
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Grid container spacing={3}>
        {/* Header */}
        <Grid item xs={12}>
          <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Development Module
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Welcome to the Development module. This area is reserved for administrative users only.
            </Typography>
          </Paper>
        </Grid>

        {/* Claude Server Authentication */}
        <Grid item xs={12}>
          <Card className={classes.card}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                <LoginIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
                Claude Server Authentication
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Authenticate with the Claude server to access AI diagnostic tools.
              </Typography>
              
              {claudeToken ? (
                <Box>
                  <Alert severity="success" sx={{ mb: 2 }}>
                    ✅ Authenticated with Claude server
                  </Alert>
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={() => {
                      localStorage.removeItem('claudeToken');
                      setClaudeToken(null);
                    }}
                  >
                    Logout
                  </Button>
                </Box>
              ) : (
                <Box>
                  {loginError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {loginError}
                    </Alert>
                  )}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleClaudeLogin}
                    disabled={loginLoading}
                    startIcon={loginLoading ? <CircularProgress size={20} /> : <LoginIcon />}
                  >
                    {loginLoading ? 'Logging in...' : 'Login to Claude Server'}
                  </Button>
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Using credentials: peter / password
                  </Typography>
                </Box>
              )}
            </Box>
          </Card>
        </Grid>

        {/* Development Tools Section */}
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                Development Tools
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Access to development and testing tools for administrators.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                onClick={() => navigate('/development/ai-diagnostics')}
                sx={{ mt: 2 }}
                fullWidth
              >
                AI Agent Diagnostics
              </Button>
            </Box>
          </Card>
        </Grid>

        {/* System Status Section */}
        <Grid item xs={12} md={6}>
          <Card className={classes.card}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                System Status
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Monitor system health and performance metrics.
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Configuration Section */}
        <Grid item xs={12}>
          <Card className={classes.card}>
            <Box sx={{ p: 3 }}>
              <Typography variant="h6" component="h2" gutterBottom>
                Configuration Management
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Manage system configurations and settings.
              </Typography>
            </Box>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
}

export default withStyles(styleSheet)(Development);
